import {
  AfterViewInit,
  Component,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SolutionSpace } from 'src/app/models/solution';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { MatDialog } from '@angular/material/dialog';
import { GeofenceFormDialogComponent } from '../geofence-form-dialog/geofence-form-dialog.component';
import { CreateGeofenceRequest, Geofence } from 'src/app/models/geofence';
import { GeofenceService } from 'src/app/services/api/geofence.service';
import { filter, switchMap } from 'rxjs';
import { BackendComponent } from 'src/app/services/api/components.service';
import { stateToUtilizationTextMap } from 'src/app/models/component';
import { UtilizationMapIcons } from 'src/app/models/map/utilizationType';
import { HumidityMapIcons } from 'src/app/models/map/humidityType';
import { MapUtil } from 'src/app/shared/map-util';
import { PopupType } from 'src/app/shared/map-view/factories/popups/FactoryPopup';
import { IPopupData } from 'src/app/shared/map-view/factories/popups/Interfaces';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { environment } from 'src/environments/environment';
import { BreakpointService } from 'src/app/services/breakpoint.service';

@Component({
  selector: 'app-zx-geofence-map',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './zx-geofence-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './zx-geofence-map.component.scss',
})
export class ZxGeofenceMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() selectedGeofence: Geofence | null = null;
  @Input() isLoadingComponents: boolean = false;
  @Input() components: BackendComponent[] = [];
  @Input() solution: SolutionSpace = SolutionSpace.Utilization;
  @Output() clearSelection = new EventEmitter<void>();

  _map!: mapboxgl.Map;
  geocoder!: MapboxGeocoder;
  draw!: MapboxDraw;
  isDrawingMode = false;
  private resizeObserver?: ResizeObserver;
  hasUnsavedPolygon = false;
  currentPolygonWKT = '';

  private selectedGeofenceLayer = 'selected-geofence';
  private selectedGeofenceSource = 'selected-geofence-source';
  private componentsLayer = 'components-layer';
  private componentsSource = 'components-source';
  private _popup: mapboxgl.Popup | undefined = undefined;
  private isHoveringOverMarker = false;
  private hasPopupBeenClosed = false;

  constructor(
    private dialog: MatDialog,
    private geofenceService: GeofenceService,
    private renderer: Renderer2,
    private snackbar: SnackbarService,
    private breakpoints: BreakpointService
  ) {}

  ngAfterViewInit() {
    mapboxgl.accessToken = environment.mapbox.accessToken;

    this._map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/polytech-as/cllqf3f2f007b01pbcsgjaus3',
      maxZoom: 19,
      minZoom: 1.25,
      center: [15.2551, 54.526], // Center of Europe
      projection: <mapboxgl.Projection>{
        name: 'mercator',
      },
      zoom: 3.5, // Zoom level to show whole Europe
    });

    // The map lives in a container that changes size (list/map toggle, rotation); keep the canvas in step with it.
    this.resizeObserver = new ResizeObserver(() => this._map.resize());
    this.resizeObserver.observe(this._map.getContainer());

    this._map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    this._map.dragRotate.disable();
    this._map.touchZoomRotate.disableRotation();

    // Initialize geocoder
    this.geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: 'Search for places...',
      proximity: {
        longitude: 8.666420098499142,
        latitude: 55.46996482176314,
      },
      // A collapsed geocoder is a search icon on phones, so it does not cover the map.
      collapsed: this.breakpoints.isPhone(),
    });

    // Add geocoder to the map. On phones it is the top-right corner: the top-left holds the list drawer's handle.
    this._map.addControl(this.geocoder, this.breakpoints.isPhone() ? 'top-right' : 'top-left');

    // Initialize Draw control
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      defaultMode: 'simple_select',
    });

    // Add draw control to map
    this._map.addControl(this.draw, 'top-left');

    // Listen to draw events
    this._map.on('draw.create', (e) => {
      this.onPolygonCreated(e.features[0]);
    });

    this._map.on('draw.update', (e) => {
      this.onPolygonUpdated(e.features[0]);
    });

    this._map.on('draw.delete', () => {
      this.hasUnsavedPolygon = false; // Update state
      this.currentPolygonWKT = ''; // Clear WKT
    });

    // Listen to geocoder events
    this.geocoder.on('result', (e) => {});

    this._map.on('load', () => {
      this.setupGeofenceLayer();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedGeofence'] && this._map) {
      this.displaySelectedGeofence();
    }

    if (changes['components'] && this._map) {
      this.displayComponents();
    }
  }

  private setupGeofenceLayer(): void {
    // Add source for selected geofence
    if (!this._map.getSource(this.selectedGeofenceSource)) {
      this._map.addSource(this.selectedGeofenceSource, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    // Add layer for selected geofence
    if (!this._map.getLayer(this.selectedGeofenceLayer)) {
      this._map.addLayer({
        id: this.selectedGeofenceLayer,
        type: 'fill',
        source: this.selectedGeofenceSource,
        paint: {
          'fill-color': '#ff910f',
          'fill-opacity': 0.3,
        },
      });

      // Add border layer
      this._map.addLayer({
        id: this.selectedGeofenceLayer + '-border',
        type: 'line',
        source: this.selectedGeofenceSource,
        paint: {
          'line-color': '#ff910f',
          'line-width': 3,
        },
      });
    }

    this.setupComponentsLayer();
  }

  private setupComponentsLayer(): void {
    // Add source for components
    if (!this._map.getSource(this.componentsSource)) {
      this._map.addSource(this.componentsSource, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    // Add layer for components (points)
    if (!this._map.getLayer(this.componentsLayer)) {
      this._map.addLayer({
        id: this.componentsLayer,
        type: 'symbol',
        source: this.componentsSource,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-size': 0.75,
          'icon-allow-overlap': true,
        },
      });

      // Add click event for components
      this._map.on('click', this.componentsLayer, (e) => {
        if (e.features && e.features[0]) {
          const point: GeoJSON.Geometry | undefined = e.features[0].geometry;

          if (point?.type !== 'Point') {
            return;
          }

          const coordinates: Array<number> = point.coordinates.slice();
          const properties = e.features[0].properties;

          // Find the full component data from the components array
          const component = this.components.find((c) => c.id === properties?.id);

          if (!component) {
            return;
          }

          // Handle longitude wrapping
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          const currentPopupLoc = this._popup?.getLngLat();
          const isSamePopUp =
            currentPopupLoc && currentPopupLoc.lng == coordinates[0] && currentPopupLoc.lat == coordinates[1];

          if (this.isHoveringOverMarker && isSamePopUp && !this.hasPopupBeenClosed) {
            this._popup?.remove();
            this.hasPopupBeenClosed = true;
          } else {
            this.showComponentPopup(<mapboxgl.LngLatLike>coordinates, component);
          }
        }
      });

      // Change cursor on hover
      this._map.on('mouseenter', this.componentsLayer, () => {
        this._map.getCanvas().style.cursor = 'pointer';
        this.isHoveringOverMarker = true;
      });

      this._map.on('mouseleave', this.componentsLayer, () => {
        this._map.getCanvas().style.cursor = '';
        this.isHoveringOverMarker = false;
      });
    }
  }

  private displaySelectedGeofence(): void {
    if (!this._map || !this._map.getSource(this.selectedGeofenceSource)) {
      return;
    }

    if (this.selectedGeofence) {
      const geojson = this.wktToGeoJSON(this.selectedGeofence.border);

      if (geojson) {
        // Update the source with the new geofence
        (this._map.getSource(this.selectedGeofenceSource) as mapboxgl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [geojson],
        });

        // Fit map to the geofence bounds
        this.fitMapToGeofence(geojson);
      }
    } else {
      // Clear the selected geofence
      (this._map.getSource(this.selectedGeofenceSource) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  private displayComponents(): void {
    if (!this._map || !this._map.getSource(this.componentsSource)) {
      return;
    }

    this.destroyPopup();

    const features = this.components.map((component) => {
      let icon: string;
      if (this.solution === SolutionSpace.Utilization) {
        icon = UtilizationMapIcons[component.state];
      } else {
        icon = HumidityMapIcons[+component.componentHealth.activeHumidityIncident];
      }

      return {
        type: 'Feature' as const,
        properties: {
          id: component.id,
          name: component.name,
          type: component.componentTypeName,
          status: stateToUtilizationTextMap[component.state],
          state: component.state,
          icon: icon,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [component.position.lng, component.position.lat],
        },
      };
    });

    (this._map.getSource(this.componentsSource) as mapboxgl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: features,
    });

    // Fit map to show both geofence and components if there are components
    if (features.length > 0) {
      this.fitMapToGeofenceAndComponents();
    }
  }

  private wktToGeoJSON(wkt: string): any {
    // Parse WKT to GeoJSON
    // This is a simple parser for POLYGON WKT
    if (wkt.startsWith('POLYGON')) {
      const coordinatesMatch = wkt.match(/POLYGON\s*\(\s*\(([^)]+)\)\s*\)/);
      if (coordinatesMatch) {
        const coordsString = coordinatesMatch[1];
        const coordinates = coordsString.split(',').map((pair) => {
          const [lng, lat] = pair.trim().split(' ').map(Number);
          return [lng, lat];
        });

        return {
          type: 'Feature',
          properties: {
            name: this.selectedGeofence?.name,
          },
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        };
      }
    }

    return null;
  }

  private fitMapToGeofence(geojson: any): void {
    if (geojson.geometry.type === 'Polygon') {
      const coordinates = geojson.geometry.coordinates[0];
      const bounds = new mapboxgl.LngLatBounds();

      coordinates.forEach((coord: [number, number]) => {
        bounds.extend(coord);
      });

      this._map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000,
      });
    }
  }

  private fitMapToGeofenceAndComponents(): void {
    const bounds = new mapboxgl.LngLatBounds();

    // Add geofence bounds
    if (this.selectedGeofence) {
      const geojson = this.wktToGeoJSON(this.selectedGeofence.border);
      if (geojson && geojson.geometry.type === 'Polygon') {
        geojson.geometry.coordinates[0].forEach((coord: [number, number]) => {
          bounds.extend(coord);
        });
      }
    }

    // Add component bounds
    this.components.forEach((component) => {
      bounds.extend([component.position.lng, component.position.lat]);
    });

    if (!bounds.isEmpty()) {
      this._map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 1000,
      });
    }
  }

  private showComponentPopup(lngLat: mapboxgl.LngLatLike, component: BackendComponent): void {
    const popupData = this.createPopupData(component);
    const popupHtml = MapUtil.getPopup(this.renderer, PopupType.Detailed, this.solution, popupData);

    this._popup = new mapboxgl.Popup().setLngLat(lngLat).setHTML(popupHtml).addTo(this._map);

    this.hasPopupBeenClosed = false;

    this._popup.on('close', () => {
      if (!this.isHoveringOverMarker) {
        this.hasPopupBeenClosed = true;
      }
    });
  }

  private createPopupData(component: BackendComponent): IPopupData {
    return {
      name: component.name,
      state: component.state,
      type: component.componentTypeDescription
        ? `${component.componentTypeName} - ${component.componentTypeDescription}`
        : component.componentTypeName,
      id: component.id,
      componentHealth: component.componentHealth,
      projectName: component.projectName,
      projectId: component.projectId,
    };
  }

  private destroyPopup(): void {
    if (this._popup !== undefined) {
      this._popup.remove();
    }
  }

  toggleDrawMode() {
    this.isDrawingMode = !this.isDrawingMode;

    if (this.isDrawingMode) {
      this.clearGeofenceLayers();
      // Enter polygon drawing mode
      this.draw.changeMode('draw_polygon');
    } else {
      // Exit drawing mode
      this.draw.changeMode('simple_select');
    }
  }

  /** Closes the polygon being drawn (Draw fires draw.create for a valid shape); the touch way to end a shape. */
  finishShape(): void {
    this.draw.changeMode('simple_select');
    this.isDrawingMode = false;
  }

  public refreshSelectedGeofence(): void {
    this.displaySelectedGeofence();
    this.displayComponents();
  }

  clearPolygons() {
    this.draw.deleteAll();
    this.clearGeofenceLayers();
    this.destroyPopup();

    // Reset state
    this.isDrawingMode = false;
    this.hasUnsavedPolygon = false;
    this.currentPolygonWKT = '';

    // Emit event to clear selection in parent
    this.clearSelection.emit();
  }

  private clearGeofenceLayers(): void {
    if (!this._map) return;

    // Clear geofence layer
    if (this._map.getSource(this.selectedGeofenceSource)) {
      (this._map.getSource(this.selectedGeofenceSource) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }

    // Clear components layer
    if (this._map.getSource(this.componentsSource)) {
      (this._map.getSource(this.componentsSource) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  saveGeofence() {
    if (!this.hasUnsavedPolygon || !this.currentPolygonWKT) {
      return;
    }

    const dialogRef = this.dialog.open(GeofenceFormDialogComponent, {
      width: '500px',
      data: { wkt: this.currentPolygonWKT },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((r): r is CreateGeofenceRequest => !!r),
        switchMap((result) => this.geofenceService.createGeofence(result))
      )
      .subscribe({
        next: (created) => {
          this.draw.deleteAll();
          this.clearGeofenceLayers();

          // Reset state
          this.hasUnsavedPolygon = false;
          this.currentPolygonWKT = '';
          this.isDrawingMode = false;
        },
        error: (err) => {
          this.snackbar.openSnackBar(
            {
              title: 'Error',
              message: 'Error saving geofence. Please try again later.',
            },
            'ERROR'
          );
        },
      });
  }

  onPolygonCreated(feature: any) {
    const wkt = this.convertToWKT(feature);
    this.currentPolygonWKT = wkt;
    this.hasUnsavedPolygon = true;
    this.isDrawingMode = false;
  }

  onPolygonUpdated(feature: any) {
    const wkt = this.convertToWKT(feature);
    this.currentPolygonWKT = wkt;
    this.hasUnsavedPolygon = true;
  }

  convertToWKT(feature: any): string {
    if (feature.geometry.type === 'Polygon') {
      const coordinates = feature.geometry.coordinates[0]; // First ring (outer boundary)
      const wktCoords = coordinates.map((coord: number[]) => `${coord[0]} ${coord[1]}`).join(', ');
      return `POLYGON((${wktCoords}))`;
    }
    return '';
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    if (this._map) {
      this._map.remove();
    }
  }
}
